import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function InfrastructureOptimizationPage() {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleConsultationSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Get all selected challenges
    const challenges = formData.getAll('challenges');
    formData.delete('challenges');
    formData.append('challenges', challenges.join(', '));

    try {
      const response = await fetch('https://readdy.ai/api/form/d50rrd5mdo3ncn62h8pg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setShowSuccessModal(true);
        form.reset();
        setCharCount(0);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleChecklistSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d50rrd5mdo3ncn62h8pg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setShowChecklistModal(false);
        form.reset();
        alert('Thank you! Your assessment checklist has been sent to your email.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const scrollToAssessmentTools = () => {
    const element = document.getElementById('assessment-tools');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToConsultationForm = () => {
    const element = document.getElementById('consultation-form-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section 
        className="relative w-full py-20 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/01780f4775426c9a1fc2e91e675bfb0e.jpg)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="w-full max-w-2xl bg-white/95 p-8 rounded-lg shadow-lg">
            <div className="mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                <div className="w-4 h-4 flex items-center justify-center mr-2">
                  <i className="ri-settings-3-line"></i>
                </div>
                Infrastructure Optimization
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Optimize Your IT Infrastructure for Peak Performance
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              Transform your technology foundation with our comprehensive infrastructure assessment, modernization, and optimization services. Reduce costs, improve performance, and future-proof your business.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={scrollToConsultationForm}
                className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium inline-flex items-center hover:bg-primary/90 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-calendar-check-line"></i>
                </div>
                Schedule Assessment
              </button>
              <button
                onClick={scrollToAssessmentTools}
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-button whitespace-nowrap font-medium inline-flex items-center hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-calculator-line"></i>
                </div>
                Free Assessment Tool
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Infrastructure Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Comprehensive Infrastructure Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our infrastructure optimization services help you build a robust, scalable, and efficient IT foundation that supports your business growth and innovation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-search-eye-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Infrastructure Assessment</h3>
              <p className="text-gray-600 mb-6">
                Comprehensive evaluation of your current IT infrastructure to identify bottlenecks, security vulnerabilities, and optimization opportunities.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Network performance analysis</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Security vulnerability assessment</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Capacity planning &amp; forecasting</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Cost optimization analysis</span>
                </li>
              </ul>
              <button className="text-primary font-medium flex items-center hover:text-primary/80 transition-colors cursor-pointer">
                <span>Learn More</span>
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-green-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-refresh-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Modernization Solutions</h3>
              <p className="text-gray-600 mb-6">
                Upgrade and modernize your infrastructure with the latest technologies to improve performance, security, and scalability.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Cloud migration &amp; hybrid solutions</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Legacy system modernization</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Network infrastructure upgrade</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Automation implementation</span>
                </li>
              </ul>
              <button className="text-green-600 font-medium flex items-center hover:text-green-600/80 transition-colors cursor-pointer">
                <span>Learn More</span>
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                  <i className="ri-speed-up-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Performance Optimization</h3>
              <p className="text-gray-600 mb-6">
                Fine-tune your infrastructure for maximum efficiency, reliability, and performance to support your business operations.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-purple-600 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Performance monitoring &amp; tuning</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-purple-600 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Resource allocation optimization</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-purple-600 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Disaster recovery planning</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-purple-600 mt-0.5 mr-3">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700">Continuous improvement</span>
                </li>
              </ul>
              <button className="text-purple-600 font-medium flex items-center hover:text-purple-600/80 transition-colors cursor-pointer">
                <span>Learn More</span>
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Measurable Impact Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-6 text-center">Infrastructure Optimization Benefits</h3>
                <div className="relative">
                  <svg viewBox="0 0 400 400" className="w-full h-auto">
                    <circle cx="200" cy="200" r="120" fill="none" stroke="#E5E7EB" strokeWidth="40" />
                    <circle cx="200" cy="200" r="120" fill="none" stroke="#0066CC" strokeWidth="40" strokeDasharray="251.2 502.4" strokeDashoffset="0" transform="rotate(-90 200 200)" />
                    <circle cx="200" cy="200" r="120" fill="none" stroke="#10B981" strokeWidth="40" strokeDasharray="188.4 502.4" strokeDashoffset="-251.2" transform="rotate(-90 200 200)" />
                    <circle cx="200" cy="200" r="120" fill="none" stroke="#F59E0B" strokeWidth="40" strokeDasharray="62.8 502.4" strokeDashoffset="-439.6" transform="rotate(-90 200 200)" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900">100%</div>
                      <div className="text-sm text-gray-600">Optimized</div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-primary rounded-full mr-3"></div>
                      <span className="text-sm text-gray-700">Cost Reduction</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">35%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-green-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-700">Performance Improvement</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">60%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-orange-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-700">Security Enhancement</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">90%</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <span className="text-primary font-semibold mb-2 block">PROVEN RESULTS</span>
              <h2 className="text-3xl font-bold mb-6">Measurable Impact on Your Business</h2>
              <p className="text-gray-600 mb-8">
                Our infrastructure optimization services deliver tangible results that directly impact your bottom line and operational efficiency.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
                    <div className="w-6 h-6 flex items-center justify-center text-white">
                      <i className="ri-money-dollar-circle-line"></i>
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold mb-2">Cost Reduction</h4>
                  <p className="text-3xl font-bold text-primary mb-1">35%</p>
                  <p className="text-gray-600 text-sm">Average reduction in IT operational costs</p>
                </div>
                <div className="bg-green-50 rounded-lg p-6">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                    <div className="w-6 h-6 flex items-center justify-center text-white">
                      <i className="ri-rocket-line"></i>
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold mb-2">Performance Boost</h4>
                  <p className="text-3xl font-bold text-green-600 mb-1">60%</p>
                  <p className="text-gray-600 text-sm">Improvement in system performance</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-6">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
                    <div className="w-6 h-6 flex items-center justify-center text-white">
                      <i className="ri-shield-check-line"></i>
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold mb-2">Security Enhancement</h4>
                  <p className="text-3xl font-bold text-purple-600 mb-1">90%</p>
                  <p className="text-gray-600 text-sm">Reduction in security vulnerabilities</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-6">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mb-4">
                    <div className="w-6 h-6 flex items-center justify-center text-white">
                      <i className="ri-time-line"></i>
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold mb-2">Faster Deployment</h4>
                  <p className="text-3xl font-bold text-orange-600 mb-1">50%</p>
                  <p className="text-gray-600 text-sm">Faster deployment of new services</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Assessment Tools */}
      <section className="py-16 bg-gray-50" id="assessment-tools">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Free Infrastructure Assessment Tools</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Get started with our comprehensive assessment tools to evaluate your current infrastructure and identify optimization opportunities.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-questionnaire-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Infrastructure Health Check</h3>
              <p className="text-gray-600 mb-6">
                Quick 10-minute assessment to evaluate your infrastructure's current state and identify immediate improvement areas.
              </p>
              <div className="mb-6">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <div className="w-4 h-4 flex items-center justify-center mr-2">
                    <i className="ri-time-line"></i>
                  </div>
                  <span>10 minutes</span>
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <div className="w-4 h-4 flex items-center justify-center mr-2">
                    <i className="ri-file-list-line"></i>
                  </div>
                  <span>Instant report</span>
                </div>
              </div>
              <button className="w-full bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer">
                Start Health Check
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <div className="w-16 h-16 bg-green-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-calculator-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">ROI Calculator</h3>
              <p className="text-gray-600 mb-6">
                Calculate the potential return on investment for your infrastructure optimization project with our interactive tool.
              </p>
              <div className="mb-6">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <div className="w-4 h-4 flex items-center justify-center mr-2">
                    <i className="ri-time-line"></i>
                  </div>
                  <span>5 minutes</span>
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <div className="w-4 h-4 flex items-center justify-center mr-2">
                    <i className="ri-money-dollar-circle-line"></i>
                  </div>
                  <span>Cost savings estimate</span>
                </div>
              </div>
              <button className="w-full bg-green-600 text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-green-600/90 transition-colors cursor-pointer">
                Calculate ROI
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <div className="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                  <i className="ri-security-scan-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Security Assessment</h3>
              <p className="text-gray-600 mb-6">
                Comprehensive security evaluation to identify vulnerabilities and compliance gaps in your infrastructure.
              </p>
              <div className="mb-6">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <div className="w-4 h-4 flex items-center justify-center mr-2">
                    <i className="ri-time-line"></i>
                  </div>
                  <span>15 minutes</span>
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <div className="w-4 h-4 flex items-center justify-center mr-2">
                    <i className="ri-shield-check-line"></i>
                  </div>
                  <span>Security score &amp; recommendations</span>
                </div>
              </div>
              <button className="w-full bg-purple-600 text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-purple-600/90 transition-colors cursor-pointer">
                Start Assessment
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation Form */}
      <section className="py-16 bg-gray-50" id="consultation-form-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Request Infrastructure Consultation</h2>
              <p className="text-gray-600">
                Get expert advice on optimizing your infrastructure. Our specialists will analyze your needs and provide tailored recommendations.
              </p>
            </div>
            <form
              onSubmit={handleConsultationSubmit}
              className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 space-y-6"
              data-readdy-form
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    id="company_name"
                    name="company_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder="Enter your company name"
                  />
                </div>
                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                    Industry *
                  </label>
                  <div className="relative">
                    <select
                      id="industry"
                      name="industry"
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm appearance-none"
                    >
                      <option value="">Select your industry</option>
                      <option value="technology">Technology</option>
                      <option value="finance">Finance &amp; Banking</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="manufacturing">Manufacturing</option>
                      <option value="retail">Retail &amp; E-commerce</option>
                      <option value="education">Education</option>
                      <option value="government">Government</option>
                      <option value="other">Other</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Name *
                  </label>
                  <input
                    id="contact_name"
                    name="contact_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label htmlFor="job_title" className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title *
                  </label>
                  <input
                    id="job_title"
                    name="job_title"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder="Enter your job title"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Business Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder="Enter your business email"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company_size" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Size *
                  </label>
                  <div className="relative">
                    <select
                      id="company_size"
                      name="company_size"
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm appearance-none"
                    >
                      <option value="">Select company size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="501-1000">501-1000 employees</option>
                      <option value="1000+">1000+ employees</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="current_budget" className="block text-sm font-medium text-gray-700 mb-2">
                    IT Budget Range
                  </label>
                  <div className="relative">
                    <select
                      id="current_budget"
                      name="current_budget"
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm appearance-none"
                    >
                      <option value="">Select budget range</option>
                      <option value="under-50k">Under $50K</option>
                      <option value="50k-100k">$50K - $100K</option>
                      <option value="100k-250k">$100K - $250K</option>
                      <option value="250k-500k">$250K - $500K</option>
                      <option value="500k-1m">$500K - $1M</option>
                      <option value="over-1m">Over $1M</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Infrastructure Challenges (Select all that apply) *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="challenges"
                      value="Poor performance & slow systems"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-3 text-sm text-gray-700">Poor performance &amp; slow systems</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="challenges"
                      value="Frequent downtime & outages"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-3 text-sm text-gray-700">Frequent downtime &amp; outages</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="challenges"
                      value="Security vulnerabilities"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-3 text-sm text-gray-700">Security vulnerabilities</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="challenges"
                      value="High operational costs"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-3 text-sm text-gray-700">High operational costs</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="challenges"
                      value="Scalability limitations"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-3 text-sm text-gray-700">Scalability limitations</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="challenges"
                      value="Compliance requirements"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-3 text-sm text-gray-700">Compliance requirements</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="challenges"
                      value="Legacy system modernization"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-3 text-sm text-gray-700">Legacy system modernization</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="challenges"
                      value="Cloud migration needs"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-3 text-sm text-gray-700">Cloud migration needs</span>
                  </label>
                </div>
              </div>

              <div>
                <label htmlFor="project_details" className="block text-sm font-medium text-gray-700 mb-2">
                  Project Details &amp; Requirements
                </label>
                <textarea
                  id="project_details"
                  name="project_details"
                  rows={4}
                  maxLength={500}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                  placeholder="Please describe your current infrastructure, specific challenges, and optimization goals..."
                ></textarea>
                <div className="mt-2 text-sm text-gray-500 flex items-center">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-information-line"></i>
                  </div>
                  <span>{charCount}/500 characters</span>
                </div>
              </div>

              <div>
                <label htmlFor="preferred_timeline" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Timeline
                </label>
                <div className="relative">
                  <select
                    id="preferred_timeline"
                    name="preferred_timeline"
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm appearance-none"
                  >
                    <option value="">Select timeline</option>
                    <option value="immediate">Immediate (within 1 month)</option>
                    <option value="quarter">This quarter (1-3 months)</option>
                    <option value="half-year">Next 6 months</option>
                    <option value="year">Within a year</option>
                    <option value="planning">Planning phase</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="privacy_consent"
                  name="privacy_consent"
                  required
                  className="w-4 h-4 mt-1 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <label htmlFor="privacy_consent" className="ml-3 text-sm text-gray-600">
                  I agree to be contacted by Thyamis regarding this consultation request and consent to the processing of my data according to the{' '}
                  <a href="#" className="text-primary hover:underline">
                    Privacy Policy
                  </a>{' '}
                  *
                </label>
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  className="bg-primary text-white px-8 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors inline-flex items-center cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-send-plane-line"></i>
                  </div>
                  Submit Consultation Request
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #0066CC 0%, #0052A3 100%)' }}>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Ready to Optimize Your Infrastructure?</h2>
            <p className="text-blue-100 mb-8">
              Don't let outdated infrastructure hold your business back. Our experts are ready to help you build a modern, efficient, and scalable IT foundation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={scrollToConsultationForm}
                className="bg-white text-primary px-6 py-3 rounded-button whitespace-nowrap font-medium inline-flex items-center hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-calendar-check-line"></i>
                </div>
                Schedule Free Consultation
              </button>
              <button
                onClick={scrollToAssessmentTools}
                className="bg-transparent border border-white text-white px-6 py-3 rounded-button whitespace-nowrap font-medium inline-flex items-center hover:bg-white/10 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-calculator-line"></i>
                </div>
                Try Assessment Tools
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSuccessModal(false)}></div>
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative z-10">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-green-500">
                  <i className="ri-check-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Request Submitted!</h3>
              <p className="text-gray-600 mb-6">
                Thank you for your consultation request. Our infrastructure specialists will contact you within 24 hours to schedule your assessment.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="bg-primary text-white px-6 py-2 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}